import { computed, makeObservable, observable } from 'mobx'

import { IReportTemplate } from '~/client/graph'
import {
  IModeSettings,
  getModeSettings,
} from '~/client/src/desktop/components/HeaderBar/components/ViewModeToggle/ViewModeToggle'
import HeaderBarStore from '~/client/src/desktop/components/HeaderBar/HeaderBar.store'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import ViewModes from '~/client/src/desktop/enums/ViewModes'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DefaultApps from '~/client/src/shared/enums/DefaultApps'
import { getReportFileTypeLabel } from '~/client/src/shared/enums/ReportFileTypes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { LOAD_REPORT_TEMPLATE } from '~/client/src/shared/stores/EventStore/eventConstants'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import { FORMS_REPORT_TEMPLATE_ID } from '~/client/src/shared/utils/usefulStrings'

import LogisticsStore from '../Logistics/Logistics.store'

export default class LogisticsHeaderBarStore extends HeaderBarStore {
  @observable public isMapViewDisabled: boolean = false

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly logisticsStore: LogisticsStore,
    isMapViewDisabled: boolean,
  ) {
    super(eventsStore.appState)

    this.isMapViewDisabled = isMapViewDisabled

    this.viewState = eventsStore.appState.formsList
    makeObservable(this)
  }

  public get logisticsForceCloseMap(): { [filterType: string]: boolean } {
    const { fieldsMap } = this.state.workflowsFilters

    return this.getForceCloseMap(fieldsMap)
  }

  private getForceCloseMap(fieldsMap: { [filterType: string]: UIFilterInfo }): {
    [filterType: string]: boolean
  } {
    return Object.keys(fieldsMap).reduce((map, filterType) => {
      map[filterType] = this.openedPopup && this.openedPopup !== filterType
      return map
    }, {})
  }

  public get isCalendarViewMode(): boolean {
    return this.viewMode === ViewModes.Calendar
  }

  public get isMapViewMode(): boolean {
    return this.viewMode === ViewModes.Map
  }

  public get isListViewMode(): boolean {
    return this.viewMode === ViewModes.List
  }

  public get isGanttViewMode(): boolean {
    return this.viewMode === ViewModes.Gantt
  }

  @computed
  public get datePickerStates() {
    switch (this.viewMode) {
      case ViewModes.Gantt:
        return this.logisticsStore.ganttDatePickerStates
      case ViewModes.Calendar:
        return this.logisticsStore.calendarDatePickerStates
      case ViewModes.List:
        return this.logisticsStore.listDatePickerStates
      case ViewModes.Map:
        return this.logisticsStore.listDatePickerStates
      default:
        return []
    }
  }

  public get viewModes(): IModeSettings[] {
    return [
      getModeSettings(ViewModes.Calendar, this.isCalendarDisabled),
      getModeSettings(ViewModes.List, false),
      getModeSettings(ViewModes.Gantt),
      getModeSettings(ViewModes.Map, this.isMapViewDisabled),
    ]
  }

  public setViewMode = (mode: ViewModes): void => {
    localStorage.setItem(DefaultApps.formsViewKey, mode)
  }

  public setInitialDateValue() {
    this.logisticsStore.setInitialDateValue()
  }

  public loadReportTemplate = (): void => {
    this.eventsStore.dispatch(
      LOAD_REPORT_TEMPLATE,
      FORMS_REPORT_TEMPLATE_ID,
      this.reportTemplateReceived,
    )
  }

  public getDatePickerModeDisplayName = (mode: TwoMonthsDatePickerMode) => {
    if (!this.isGanttViewMode) return null

    switch (mode) {
      case TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY:
        return Localization.translator.oneDay
      case TwoMonthsDatePickerMode.ONE_DAY:
        return Localization.translator.oneDay
      case TwoMonthsDatePickerMode.FIXED_WEEK:
        return Localization.translator.week
      case TwoMonthsDatePickerMode.FIXED_TWO_WEEKS:
        return Localization.translator.twoWeeks
      case TwoMonthsDatePickerMode.FIXED_THREE_WEEKS:
        return Localization.translator.threeWeeks
      case TwoMonthsDatePickerMode.FIXED_FOUR_WEEKS:
        return Localization.translator.fourWeeks
      case TwoMonthsDatePickerMode.FIXED_SIX_WEEKS:
        return Localization.translator.sixWeeks
    }
  }

  private reportTemplateReceived = (reportTemplate: IReportTemplate): void => {
    this.additionalReportFiles = reportTemplate.files.map((file, i) => ({
      value: file.fileId,
      format: file.fileType,
      name: file.name,
      label: getReportFileTypeLabel(file.fileId) || file.name,
      isChecked: i === 0,
    }))
  }

  private get isCalendarDisabled(): boolean {
    return this.state.isWorkflowsDisabled
  }
}
